import React from 'react'

import * as c from '../../../../common'
import * as r from '../../../../react-utils'

import * as MuiIcons from '@mui/icons-material'

import { FormatPrice } from '../../../helpers/FormatPrice'
import { productModelUrl } from '../../../img/productModels/productModelMap'
import { productPhotoMap } from '../../../img/productPhotos/productPhotoMap'
import {
  SUMMARY_DETAIL_FIELDS,
  getRowProductPhotos,
  getS4CameraNoteBody,
  getS4CameraSummaryLine,
} from './jobSummaryProducts'

//@ts-ignore
import gStyles from '../../../global.module.css'

//@ts-ignore
import styles from './Cost.module.css'

const photoFor = (type: string): string | undefined => productPhotoMap[type]

export interface ICost {
  customer: c.Customer
  name: keyof c.Customer
  selected: boolean
  value: string
}

export interface CostProps extends ICost {
  customerID: string
  isPDF: boolean
  onEfficiencyEdit: (field: string) => void
  /**
   * Opens the Job Summary's single ProductViewer on this product (Monday
   * 13246279222: the viewer steps through every product, so it lives in
   * Summary, not in each row). Must be stable: this row is memoized.
   */
  onOpenProduct: (field: string, type: string) => void
  readOnly?: boolean
}

export interface SummaryNotes {
  /** Generated by the app (`efficiencyToCalculator` / `securityAutoNotes`). */
  readonly auto: string
  /** Typed by a person: `<field>_extra_notes`, or a field with no calculator. */
  readonly custom: string
}

/**
 * The service notes of one category, split by where they came from - not by
 * what they say (Jaron, Slack 2026-09-11: notes the rep adds "that aren't
 * auto-populated" print bold, so the page has to know which are which).
 *
 * An efficiency that has a calculator owns its `<field>_notes`: the input is
 * disabled in `CheckGroup` (`disabled={hasAutoNotes}`) and the text is written
 * by the calculator, so it is auto and `<field>_extra_notes` is the rep's own.
 * A field with no calculator (`custom_*` items, pool pump, ...) has its
 * `<field>_notes` typed by hand, so all of it is custom.
 */
export const getSummaryNotes = (
  customer: c.Customer,
  field: c.CustomerField,
): SummaryNotes => {
  const safe = (text: string): string =>
    SUMMARY_DETAIL_FIELDS[field] ? c.alarmSafeNotesBlock(text) : text
  const notes = c.customerNotes(customer, field)
  const extraNotes = (
    (customer[(field + '_extra_notes') as c.CustomerField] as string) || ''
  ).trim()
  const hasCalculator = !!(
    c.efficiencyToCalculator as Readonly<Record<string, unknown>>
  )[field]

  return hasCalculator
    ? { auto: safe(notes), custom: safe(extraNotes) }
    : { auto: '', custom: safe(c.joinProductNotes(notes, extraNotes)) }
}

export default React.memo<CostProps>(
  ({
    customer,
    customerID,
    isPDF,
    name: field,
    onEfficiencyEdit,
    onOpenProduct,
    readOnly,
    value,
  }) => {
    const detailField = SUMMARY_DETAIL_FIELDS[field as c.CustomerField]
    // Stable per Cost instance (`field` never changes after mount, see
    // Summary.tsx's `key={cost.name}`) — same conditional-hook precedent as
    // react-utils.tsx's useRowable() call sites.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { rows: detailRows } = detailField
      ? // eslint-disable-next-line react-hooks/rules-of-hooks
        r.useRowable(customerID, detailField)
      : { rows: c.emptyArr }
    // The S4 photo moves with its named sub-row so the product has a visible
    // place in Job Summary even when other WiFi-camera products share its row.
    // Same helper Summary uses to list the viewer's products, so the arrows
    // follow exactly the thumbnails on screen.
    const { categoryPhotos, s4Photo: s4CameraPhoto } = React.useMemo(
      () => getRowProductPhotos(detailRows as readonly object[], photoFor),
      [detailRows],
    )
    const s4CameraSummary = getS4CameraSummaryLine(
      detailRows as readonly Readonly<{ type: string; qty: unknown }>[],
    )
    const summaryNotes = getSummaryNotes(customer, field)
    const s4CameraBody = s4CameraSummary
      ? getS4CameraNoteBody(summaryNotes.auto)
      : ''
    // Notes column (13213613154): every product, each with up to 3 of its
    // notes; the full text stays on the NOTES page. The S4 block is left out when it has its own sub-row
    // below, so it is not printed twice.
    const shortNotes = c.shortProductNotes(
      summaryNotes.auto,
      summaryNotes.custom,
      field.startsWith('security_'),
      c.SHORT_NOTES_MAX_LINES,
      !!s4CameraSummary,
    )
    const [enabledVal, enabledSet] = r.useCustomerField(
      customerID,
      (field + '_show') as c.CustomerField,
    )
    const enabledToggle = React.useCallback((): void => {
      if (readOnly) return
      enabledSet((curr) => (curr === 0 ? 1 : 0))
    }, [enabledSet, readOnly])

    const handleEfficiencyEdit = React.useCallback(() => {
      if (readOnly) return
      onEfficiencyEdit(field)
    }, [field, onEfficiencyEdit, readOnly])

    return (
      <>
        <tr className={styles['root']}>
          {!isPDF && (
            <td className={gStyles['cursor-pointer']} onClick={enabledToggle}>
              <input
                checked={enabledVal === 1}
                className={gStyles['cursor-pointer']}
                onChange={c.emptyFn}
                type="checkbox"
              />
            </td>
          )}

          <td className={styles['name']}>
            <span onClick={enabledToggle}>
              {field.startsWith('custom_')
                ? (customer[
                    (field + '_name') as keyof c.Customer
                  ] as unknown as React.ReactElement)
                : // This row renders unconditionally, including isPDF and the
                // customer-facing ?mode=sign view (V43) — Jaron/Luis, 09-03:
                // the word "security" can't appear on anything the customer
                // reads, category name or not (T56). getFieldLabel resolves
                // security_system to "Security System" for internal/dropdown
                // use, same as the scope-line generator this mirrors
                // (nonSecurityScopeLines' sibling, the Smart Home scope line).
                // Found 12993405905: this row still leaked the internal name
                // while the contract PDF already showed Smart Home for the
                // same line.
                field === 'security_system'
                ? 'Smart Home'
                : c.getFieldLabel(field)}
            </span>
          </td>

          {/* Internal-only price column — Jaron (Slack #development-dpt,
            2026-07-22): "I have no idea what is being sold here" on a Job
            Summary screenshot with a lump total but no per-line price.
            `value` was already computed/passed by Summary.tsx but never
            rendered here. Gated behind !isPDF (customer-facing exported PDF)
            AND !readOnly (customer-facing live view during public token
            signing, `?mode=sign` — a separate flag from isPDF, see V43) so
            this only ever renders in the internal rep-facing editor, never
            to the customer. */}
          {!isPDF && !readOnly && (
            <td className={styles['price']} onClick={enabledToggle}>
              {FormatPrice(c.parseFloating(value), '$')}
            </td>
          )}

          {/* Short notes (Monday 13213613154, Oct 5 meeting): back in the Job
            Summary after 9786b816 moved them to the NOTES page. No chevron or
            overlay this time — the lines are already capped, so the cell
            wraps and prints in full in the PDF. */}
          <td className={styles['notes-td']}>
            <ul className={styles['notes-ul']}>
              {shortNotes.map(({ kind, text }, i) =>
                kind === 'separator' ? (
                  <li className={styles['note-separator']} key={field + i} />
                ) : (
                  <li
                    className={
                      kind === 'product'
                        ? styles['note-line-product']
                        : kind === 'custom'
                        ? styles['note-line-custom']
                        : styles['note-line']
                    }
                    key={field + i}
                  >
                    {text}
                  </li>
                ),
              )}
            </ul>
          </td>

          {/* Dedicated photo column — keeps product thumbnails in their own
            Job Summary cell.

            Tapping a thumbnail opens ProductViewer: full-screen on phones,
            one product at a time, with a tab per kind of media the product
            has (Products photos with pinch-zoom/pan, Scene, Video, 3D Model).
            It replaced the Fancybox gallery here (Monday 13110542552 started
            that one, 12993381840 the tabs). Products with a .glb keep the 3D
            badge on their thumbnail. */}
          {!isPDF && (
            <td className={styles['photos-td']}>
              {categoryPhotos.map((photo) => (
                <button
                  aria-label={`View ${c.resolveAlarmSafeProductName(
                    photo.type,
                  )}`}
                  className={styles['photo-button']}
                  key={photo.type}
                  onClick={() => onOpenProduct(field, photo.type)}
                  type="button"
                >
                  <img
                    alt=""
                    className={styles['photo-in-button']}
                    src={photo.url}
                  />
                  {productModelUrl(photo.type) && (
                    <span className={styles['photo-badge']}>3D</span>
                  )}
                </button>
              ))}
            </td>
          )}

          {!isPDF && (
            <td
              className={gStyles['cursor-pointer']}
              onClick={handleEfficiencyEdit}
            >
              <MuiIcons.EditOutlined />
            </td>
          )}
        </tr>
        {s4CameraSummary && (
          <tr className={styles['s4-row']}>
            {/* Monday 13246307524 (Jaron, 2026-10-08): the S4 text spanned
                Category + Price + Notes and broke the table. It sits in the
                Notes column now, styled like the product notes above it:
                empty cells keep Category and Price aligned. */}
            {!isPDF && <td aria-hidden="true" />}
            <td aria-hidden="true" />
            {!isPDF && !readOnly && <td aria-hidden="true" />}
            <td className={styles['notes-td']}>
              <ul className={styles['notes-ul']}>
                <li className={styles['note-line-product']}>
                  {s4CameraSummary.quantity} x {s4CameraSummary.displayName}
                </li>
                {s4CameraBody
                  .split(' · ')
                  .filter((line) => line.length > 0)
                  .map((line, i) => (
                    <li className={styles['note-line']} key={'s4-' + i}>
                      {line}
                    </li>
                  ))}
              </ul>
            </td>
            {!isPDF && (
              <td className={styles['photos-td']}>
                {s4CameraPhoto && (
                  <button
                    aria-label={`View ${s4CameraSummary.displayName}`}
                    className={styles['photo-button']}
                    onClick={() => onOpenProduct(field, s4CameraPhoto.type)}
                    type="button"
                  >
                    <img
                      alt=""
                      className={styles['photo-in-button']}
                      src={s4CameraPhoto.url}
                    />
                  </button>
                )}
              </td>
            )}
            {!isPDF && <td aria-hidden="true" />}
          </tr>
        )}
      </>
    )
  },
)
